<script setup>
const props = defineProps({
  hasDismissal: {
    type: Boolean,
    default: false,
  },
  hasProgress: {
    type: Boolean,
    default: false,
  },
  hasProgressBegun: {
    type: Boolean,
    default: false,
  },
  hasOrbs: {
    type: Boolean,
    default: false,
  },
  isFocusOnDismiss: {
    type: Boolean,
    default: false,
  },
  question: {
    type: String,
    default: '',
  },
  correctOptionLabel: {
    type: String,
    default: '',
  },
  triviaOptions: {
    type: Array,
    default: () => [],
  },
  focusIndex: {
    type: Number,
    default: 0,
  },
  selectedIndex: {
    type: Number,
    default: 0,
  },
  triviaOutputCountdownMS: {
    type: Number,
    default: 7000,
  },
  progressDuration: {
    type: Number,
    default: 7000,
  },
});

const isCorrect = (option) => option.optionLabel === props.correctOptionLabel;
const isSelected = (option) => option.optionIndex === props.selectedIndex;
const hasNotSelected = () => props.selectedIndex === -1;
const userGotItRight = () =>
  props.triviaOptions[props.selectedIndex]?.optionLabel === props.correctOptionLabel;
</script>

<template>
  <div
    class="trivia-card trivia-output"
    :class="{
      'trivia-output--open': props.hasProgress && props.hasProgressBegun,
    }"
  >
    <div class="trivia-card__container">
      <div class="trivia-card__wrapper">
        <div class="trivia-card__contents">
          <div class="trivia-card__header">
            <span class="trivia-card__header-copy">Trivia</span>
          </div>

          <div class="trivia-card__question">
            <span
              v-if="hasNotSelected()"
              class="trivia-card__question-copy trivia-card__question-copy--result"
            >
              {{ question }}
            </span>
            <span v-else class="trivia-card__question-copy trivia-card__question-copy--result">
              {{ userGotItRight() ? "That's right!" : 'Not quite' }}
            </span>
          </div>

          <div
            class="trivia-card__results-row"
            :class="{
              'has-dismissal': props.hasDismissal,
            }"
          >
            <div
              v-for="item in props.triviaOptions"
              :key="item.optionLabel"
              class="trivia-card__outcome"
              :class="{
                'trivia-card__option--correct': isCorrect(item),
                'trivia-card__option--incorrect': isSelected(item) && !isCorrect(item),
                'trivia-card__option--dimmed': !isCorrect(item) && !isSelected(item),
              }"
            >
              <div
                class="trivia-card__option-button"
                :class="{
                  'trivia-card__option-button--correct': isCorrect(item),
                  'trivia-card__option-button--incorrect': isSelected(item) && !isCorrect(item),
                  'trivia-card__option-button--dimmed': !isCorrect(item) && !isSelected(item),
                }"
              >
                <div class="trivia-card__option-content">
                  <svg
                    v-if="isSelected(item) && isCorrect(item)"
                    class="trivia-card__option-icon trivia-card__option-check"
                    viewBox="0 0 40 40"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <circle cx="20" cy="20" r="20" fill="white" fill-opacity="0.2" />
                    <path
                      d="M7.50098 20.6879L15.1454 28.5L31.501 11.7857"
                      stroke="white"
                      stroke-width="2.50714"
                    />
                  </svg>

                  <svg
                    v-else-if="isSelected(item)"
                    class="trivia-card__option-icon trivia-card__option-x"
                    viewBox="0 0 28 28"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M7 7 L21 21 M21 7 L7 21"
                      stroke="rgba(255, 26, 26, 1)"
                      stroke-width="3"
                      stroke-linecap="round"
                    />
                  </svg>

                  <span class="trivia-card__option-text">{{ item.optionEntry }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div
          v-if="props.hasDismissal"
          class="trivia-card__dismissal"
          :class="{ active: props.isFocusOnDismiss }"
        >
          <span class="trivia-card__dismissal-copy">Dismiss</span>
        </div>

        <div v-if="props.hasProgress && props.hasProgressBegun" class="trivia-card__progress">
          <div
            class="trivia-card__progress-bar"
            :class="{
              'trivia-card__progress-bar--closed': !hasProgressBegun,
              'trivia-card__progress-bar--open': hasProgressBegun,
            }"
            :style="{ '--progress-duration': `${props.progressDuration / 1000}s` }"
          ></div>
        </div>
      </div>

      <!-- <div class="trivia-card__glow"></div> -->
    </div>
  </div>
</template>
